import { DRAW } from "../art/furniture";
import { R } from "../art/pixel";
import { type Cell, key, unkey } from "../lib/util";
import { L, S } from "./state";
import type { Ent, EntInit } from "./types";

/** Tile size in px, map size in tiles. */
export const T = 16;
export const COLS = 20;
export const ROWS = 12;

let staticSolid = new Set<string>();
let staticInteract = new Map<string, string>();
let solid = new Set<string>();
/** cell key → interaction id, including entities. */
export let interact = new Map<string, string>();
/** zoneAt[y][x] → zone letter from the level map. */
export let zoneAt: string[][] = [];

/** Pre-rendered floor, walls and furniture. */
export const bg = document.createElement("canvas");
bg.width = COLS * T;
bg.height = ROWS * T;

export const zone = (x: number, y: number): string | undefined => zoneAt[y]?.[x];

const isExit = (x: number, y: number): boolean => (L.exits ?? []).some(([a, b]) => a === x && b === y);

export function buildLevelMap(): void {
  staticSolid = new Set();
  staticInteract = new Map();
  zoneAt = L.map.map((row, y) =>
    Array.from(row, (ch, x) => {
      if (ch === "#") staticSolid.add(key(x, y));
      return ch;
    }),
  );
  for (const o of L.objects) {
    const w = o.w ?? 1;
    const h = o.h ?? 1;
    for (let i = 0; i < w; i++)
      for (let j = 0; j < h; j++) {
        if (!o.walk) staticSolid.add(key(o.x + i, o.y + j));
        if (o.label) staticInteract.set(key(o.x + i, o.y + j), o.act ?? o.id);
      }
  }
  rebuildDyn();
  paintBackground();
}

function paintBackground(): void {
  const c = bg.getContext("2d");
  if (!c) return;
  for (let y = 0; y < ROWS; y++)
    for (let x = 0; x < COLS; x++) {
      const px = x * T;
      const py = y * T;
      const z = zone(x, y) ?? "#";
      if (z === "#") {
        R(c, "#7e88b8", px, py, T, T);
        R(c, "#59628f", px, py, T, 4);
        R(c, "#4a5278", px, py + 13, T, 3);
        if ((L.windows ?? []).some(([a, b]) => a === x && b === y)) {
          R(c, "#d6dbf0", px + 2, py + 4, 12, 9);
          R(c, L.night ? "#16224f" : "#9fd0ef", px + 3, py + 5, 10, 7);
          R(c, "#d6dbf0", px + 8, py + 5, 1, 7);
        }
        if (isExit(x, y)) {
          R(c, "#6b4a34", px, py, T, T);
          R(c, "#8a5f40", px + 2, py + 2, 12, 14);
          R(c, "#e2c27a", px + 11, py + 8, 2, 2);
        }
        continue;
      }
      const [base, line, spk] = L.zones[z] ?? ["#ddd", "#ccc", "#d4d4d4"];
      R(c, base, px, py, T, T);
      R(c, line, px + 15, py, 1, T);
      R(c, line, px, py + 15, T, 1);
      if ((x * 7 + y * 3) % 5 === 0) R(c, spk, px + 5, py + 6, 2, 1);
    }
  for (const o of L.objects) (L.furniture?.[o.type] ?? DRAW[o.type])?.(c, o.x * T, o.y * T, o);
}

/** Recompute collision and interaction maps after entities move or hide. */
export function rebuildDyn(): void {
  solid = new Set(staticSolid);
  interact = new Map(staticInteract);
  for (const e of S.ents) {
    if (e.hidden) continue;
    solid.add(key(e.x, e.y));
    if (e.act) interact.set(key(e.x, e.y), e.act);
  }
}

export const inBounds = (x: number, y: number): boolean => x >= 0 && y >= 0 && x < COLS && y < ROWS;
export const passable = (x: number, y: number): boolean => inBounds(x, y) && !solid.has(key(x, y));

/** Breadth-first path (excluding the start cell) to the first cell satisfying `goal`. */
export function bfs(
  sx: number,
  sy: number,
  goal: (x: number, y: number) => boolean,
  pass: (x: number, y: number) => boolean = passable,
): Cell[] | null {
  const start = key(sx, sy);
  const q: Cell[] = [[sx, sy]];
  const prev = new Map<string, string | null>([[start, null]]);
  while (q.length) {
    const [x, y] = q.shift() as Cell;
    if (!(x === sx && y === sy) && goal(x, y)) {
      const path: Cell[] = [];
      let k: string | null | undefined = key(x, y);
      while (k && k !== start) {
        path.unshift(unkey(k));
        k = prev.get(k);
      }
      return path;
    }
    for (const [dx, dy] of [
      [1, 0],
      [-1, 0],
      [0, 1],
      [0, -1],
    ] as const) {
      const nx = x + dx;
      const ny = y + dy;
      const nk = key(nx, ny);
      if (!prev.has(nk) && pass(nx, ny)) {
        prev.set(nk, key(x, y));
        q.push([nx, ny]);
      }
    }
  }
  return null;
}

/* ---------- entities ---------- */

/** Find an entity by sprite key or id (hidden ones included). */
export const findEnt = (k: string): Ent | undefined => S.ents.find((x) => x.k === k || x.id === k);

/** Like findEnt, but the entity must exist (spawned by `Level.ents()` or `spawnAt`). */
export function ent(k: string): Ent {
  const e = findEnt(k);
  if (!e) throw new Error(`no entity ${k}`);
  return e;
}

/** Visible entity speaking as `who`, if any. */
export function entFor(who: string | null | undefined): Ent | undefined {
  if (!who || !S.live) return undefined;
  return S.ents.find((e) => !e.hidden && (e.id === who || e.k === who));
}

export function mkEnt(o: EntInit): Ent {
  return { dir: "down", path: [], moving: null, hidden: false, ...o, fx: o.x, fy: o.y };
}

/** Route an entity to `target` (or next to it when occupied). `toDoor` allows exit tiles. */
export function walkEnt(e: Ent, target: Cell, toDoor = false): Ent {
  e.hidden = false;
  const mine = key(e.x, e.y);
  const pass = (x: number, y: number): boolean => {
    if (isExit(x, y)) return toDoor || isExit(e.x, e.y);
    return inBounds(x, y) && (!solid.has(key(x, y)) || key(x, y) === mine);
  };
  const [tx, ty] = target;
  const p =
    bfs(e.x, e.y, (x, y) => x === tx && y === ty, pass) ??
    bfs(e.x, e.y, (x, y) => Math.abs(x - tx) + Math.abs(y - ty) === 1, pass);
  e.path = p ?? [];
  e.onArrive = null;
  e.face = null;
  return e;
}

/** Walk out through an exit door, then hide. */
export function leaveBy(e: Ent, door: Cell, after?: () => void): void {
  walkEnt(e, door, true);
  e.onArrive = () => {
    e.hidden = true;
    rebuildDyn();
    after?.();
  };
}

/** Place (or create) an entity at `from` and walk it to `target`. */
export function spawnAt(k: string, act: string | undefined, from: Cell, target: Cell, extra: Partial<Ent> = {}): Ent {
  let e = findEnt(k);
  if (!e) {
    e = mkEnt({ k, id: k, x: from[0], y: from[1], act, ...extra });
    S.ents.push(e);
  } else {
    [e.x, e.y] = from;
    e.fx = e.x;
    e.fy = e.y;
    e.act = act;
  }
  return walkEnt(e, target);
}

/** Interaction id → its cells. */
export function interactCells(id: string): Cell[] {
  const cells: Cell[] = [];
  for (const [k, v] of interact) if (v === id) cells.push(unkey(k));
  return cells;
}
